import { memo, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, X, Info } from 'lucide-react';
import {
  UserNotification,
  NotificationTypeConfig,
  NotificationPriority,
  NotificationPriorityConfig,
  RecipientStatus,
  NotificationChannel,
} from '@/types/notification.types';
import { useAuth } from '@/contexts/AuthContext';
import { formatDistanceToNow } from 'date-fns';
import Modal from '@/components/ui/Modal';
import { PushDetailsModal } from './PushDetailsModal';

// Get user initials from name (max 3 chars like Hrone: "ADA")
function getInitials(name: string): string {
  const trimmed = name.trim();
  if (!trimmed) return 'U';
  const parts = trimmed.split(/\s+/);
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return parts.map((p) => p.charAt(0).toUpperCase()).slice(0, 3).join('');
}

// Thresholds for "Show more" — if the message exceeds this, it's truncated.
const COMPACT_MSG_LIMIT = 60;
const FULL_MSG_LIMIT = 120;

// Convert a TTL in seconds to a human-readable string. Used to surface the
// push delivery window in the notification list — e.g. "TTL: 3 days".
function formatTtl(seconds: number): string {
  if (seconds < 60) return `${seconds}s`;
  if (seconds < 3600) return `${Math.round(seconds / 60)}m`;
  if (seconds < 86400) {
    const hours = Math.round(seconds / 3600);
    return `${hours}h`;
  }
  const days = Math.round(seconds / 86400);
  return `${days}d`;
}

interface NotificationItemProps {
  notification: UserNotification;
  onMarkAsRead?: (id: string) => void;
  onDismiss?: (id: string) => void;
  showActions?: boolean;
  compact?: boolean;
}

function NotificationItemComponent({
  notification,
  onMarkAsRead,
  onDismiss,
  showActions = true,
  compact = false,
}: NotificationItemProps) {
  const navigate = useNavigate();
  const { user } = useAuth();
  // DELIVERED (email) and READ (in-app) both mean "user has received it" — no highlight needed.
  const isRead = notification.status === RecipientStatus.READ || notification.status === RecipientStatus.DELIVERED;
  const config = NotificationTypeConfig[notification.type];
  const priorityConfig = notification.priority ? NotificationPriorityConfig[notification.priority] : null;
  const isUrgent = notification.priority === NotificationPriority.URGENT;
  const isHighPriority = isUrgent || notification.priority === NotificationPriority.HIGH;
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [showPushDetails, setShowPushDetails] = useState(false);
  const [imageError, setImageError] = useState(false);

  // Push-only display: when the notification was delivered through PUSH,
  // surface a TTL pill in the meta row and a Details button that opens the
  // PushDetailsModal (full icon image + human-readable TTL). The avatar
  // slot continues to show the recipient's profile image / initials —
  // the push icon belongs in the details modal, not on the user's avatar
  // (which would conflate "the brand sending the push" with "the user
  // receiving it").
  const isPushNotification = Array.isArray(notification.channels)
    ? notification.channels.includes(NotificationChannel.PUSH)
    : false;
  // OneSignal's default TTL is 3 days (259200s). When the admin doesn't
  // override it on send, the backend stores null — but the actual push
  // delivery still uses 3 days. Fall back to that default so the user always
  // sees an accurate TTL value for any push notification, not a missing pill.
  const effectiveTtl =
    isPushNotification
      ? (typeof notification.ttl === 'number' && notification.ttl > 0 ? notification.ttl : 259200)
      : null;
  const ttlLabel = effectiveTtl !== null ? formatTtl(effectiveTtl) : null;

  // User initials for avatar — prefer given_name[0] + family_name[0]
  const userName = user
    ? `${user.given_name || ''} ${user.family_name || ''}`.trim() || user.full_name || 'User'
    : 'User';
  const initials = user?.given_name || user?.family_name
    ? `${(user.given_name?.[0] || '').toUpperCase()}${(user.family_name?.[0] || '').toUpperCase()}` || getInitials(userName)
    : getInitials(userName);
  const profileImage = (notification.profile_image || (user as any)?.profile_image) as string | undefined;
  const showImage = !!profileImage && !imageError;

  // Reset fallback state when the source URL changes
  useEffect(() => {
    setImageError(false);
  }, [profileImage]);

  // Clicking the item body only marks it as read — NO navigation.
  const handleClick = () => {
    if (!isRead && onMarkAsRead) {
      onMarkAsRead(notification._id);
    }
  };

  const handleMarkAsRead = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onMarkAsRead && !isRead) {
      onMarkAsRead(notification._id);
    }
  };

  const handleDismiss = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onDismiss) {
      onDismiss(notification._id);
    }
  };

  // Explicit action button — opens the action_url and marks read if unread.
  const handleOpenActionUrl = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!isRead && onMarkAsRead) {
      onMarkAsRead(notification._id);
    }
    if (!notification.action_url) return;
    if (notification.action_url.match(/^https?:\/\//)) {
      window.open(notification.action_url, '_blank', 'noopener,noreferrer');
    } else {
      navigate(notification.action_url);
    }
  };

  const handleShowMore = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowMessageModal(true);
  };

  const hasActionUrl = !!notification.action_url;
  const msgLimit = compact ? COMPACT_MSG_LIMIT : FULL_MSG_LIMIT;
  const isLongMessage = notification.message.length > msgLimit;

  const timeAgo = formatDistanceToNow(new Date(notification.created_at), {
    addSuffix: true,
  });

  if (compact) {
    return (
      <>
        <div
          onClick={handleClick}
          className={`group flex items-start gap-3 p-3 cursor-pointer transition-colors hover:bg-gray-50 ${
            !isRead ? 'bg-blue-50/50' : ''
          }`}
        >
          <div className="flex-shrink-0 w-10 h-10 rounded-full bg-primary-700 flex items-center justify-center overflow-hidden">
            {showImage ? (
              <img
                src={profileImage}
                alt={userName}
                className="w-full h-full object-cover"
                onError={() => setImageError(true)}
              />
            ) : (
              <span className="text-xs font-bold text-white">{initials}</span>
            )}
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <p
                className={`text-sm truncate ${
                  !isRead ? 'font-semibold text-gray-900' : 'font-medium text-gray-700'
                }`}
              >
                {notification.title}
              </p>
              {priorityConfig && isHighPriority && (
                <span className={`inline-flex items-center gap-1 flex-shrink-0 text-[10px] font-bold px-1.5 py-0.5 rounded ${priorityConfig.bgColor} ${priorityConfig.color}`}>
                  {isUrgent && <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" />}
                  {priorityConfig.label}
                </span>
              )}
              {ttlLabel && (
                <span
                  className="inline-flex items-center flex-shrink-0 text-[10px] font-medium px-1.5 py-0.5 rounded bg-indigo-50 text-indigo-700"
                  title={`Push TTL: ${effectiveTtl}s`}
                >
                  TTL {ttlLabel}
                </span>
              )}
              {!isRead && (
                <span className="flex-shrink-0 w-2 h-2 bg-blue-500 rounded-full" />
              )}
            </div>
            <p className="text-xs text-gray-500 truncate mt-0.5">
              {notification.message}
            </p>
            {isLongMessage && (
              <button
                onClick={handleShowMore}
                className="text-xs text-primary-600 hover:text-primary-700 font-medium mt-0.5"
              >
                Show more
              </button>
            )}
            <p className="text-xs text-gray-400 mt-1">{timeAgo}</p>
          </div>

          {showActions && (
            <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
              {isPushNotification && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowPushDetails(true);
                  }}
                  className="p-1 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded"
                  title="View push details"
                >
                  <Info className="w-4 h-4" />
                </button>
              )}
              {hasActionUrl && (
                <button
                  onClick={handleOpenActionUrl}
                  className="px-2 py-1 text-xs font-medium text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50 rounded"
                >
                  Open link
                </button>
              )}
              {!isRead && (
                <button
                  onClick={handleMarkAsRead}
                  className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded"
                  title="Mark as read"
                >
                  <Check className="w-4 h-4" />
                </button>
              )}
              {onDismiss && (
                <button
                  onClick={handleDismiss}
                  className="p-1 text-gray-400 hover:text-red-500 hover:bg-gray-100 rounded"
                  title="Dismiss"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
          )}
        </div>

        {/* Full message modal */}
        <Modal
          isOpen={showMessageModal}
          onClose={() => setShowMessageModal(false)}
          title={notification.title}
          size="md"
        >
          <div className="overflow-y-auto custom-scrollbar" style={{ maxHeight: '60vh' }}>
            <p className="text-sm text-gray-800 whitespace-pre-wrap break-words leading-relaxed">
              {notification.message}
            </p>
          </div>
        </Modal>

        <PushDetailsModal
          isOpen={showPushDetails}
          onClose={() => setShowPushDetails(false)}
          title={notification.title}
          iconUrl={notification.icon_url}
          ttl={effectiveTtl}
        />
      </>
    );
  }

  const hasFullActions = showActions || hasActionUrl || isPushNotification;
  const fullActions = (
    <>
      {isPushNotification && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            setShowPushDetails(true);
          }}
          className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50 rounded-lg transition-colors"
          title="View push details"
        >
          <Info className="w-4 h-4" />
          Details
        </button>
      )}
      {hasActionUrl && (
        <button
          onClick={handleOpenActionUrl}
          className="px-3 py-1.5 text-sm font-medium text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50 rounded-lg transition-colors"
        >
          Open link
        </button>
      )}
      {showActions && !isRead && (
        <button
          onClick={handleMarkAsRead}
          className="p-2 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded-lg transition-colors"
          title="Mark as read"
        >
          <Check className="w-4 h-4" />
        </button>
      )}
    </>
  );

  return (
    <>
      <div
        onClick={handleClick}
        className={`group flex items-start gap-3 sm:gap-4 p-3 sm:p-4 border-b border-gray-100 cursor-pointer transition-colors hover:bg-gray-50 ${
          !isRead ? 'bg-blue-50/30' : ''
        }`}
      >
        <div className="flex-shrink-0 w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-primary-700 flex items-center justify-center overflow-hidden">
          {showImage ? (
            <img
              src={profileImage}
              alt={userName}
              className="w-full h-full object-cover"
              onError={() => setImageError(true)}
            />
          ) : (
            <span className="text-sm font-bold text-white">{initials}</span>
          )}
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <span
              className={`text-xs font-medium px-2 py-0.5 rounded-full ${config.bgColor} ${config.color}`}
            >
              {config.label}
            </span>
            {priorityConfig && (
              <span
                className={`inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full ${priorityConfig.bgColor} ${priorityConfig.color}`}
              >
                {isUrgent && <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" />}
                {priorityConfig.label}
              </span>
            )}
            {ttlLabel && (
              <span
                className="inline-flex items-center text-xs font-medium px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-700"
                title={`Push Time To Live: ${effectiveTtl}s`}
              >
                TTL {ttlLabel}
              </span>
            )}
            {!isRead && (
              <span className="flex-shrink-0 w-2 h-2 bg-blue-500 rounded-full" />
            )}
          </div>

          <h4
            className={`text-sm mb-1 break-words ${
              !isRead ? 'font-semibold text-gray-900' : 'font-medium text-gray-800'
            }`}
          >
            {notification.title}
          </h4>

          <p className="text-sm text-gray-600 line-clamp-2 break-words">{notification.message}</p>
          {isLongMessage && (
            <button
              onClick={handleShowMore}
              className="text-xs text-primary-600 hover:text-primary-700 font-medium mt-1"
            >
              Show more
            </button>
          )}

          <p className="text-xs text-gray-400 mt-2">{timeAgo}</p>

          {/* Phones: actions sit under the message, always visible — there is no
              hover on touch, and a right-hand column squeezed the title to one
              word per line. */}
          {hasFullActions && (
            <div className="sm:hidden flex flex-wrap items-center gap-1 mt-2 -ml-3">
              {fullActions}
            </div>
          )}
        </div>

        {/* sm and up: actions on the right, revealed on hover (unchanged) */}
        {hasFullActions && (
          <div className="hidden sm:flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
            {fullActions}
          </div>
        )}
      </div>

      {/* Full message modal */}
      <Modal
        isOpen={showMessageModal}
        onClose={() => setShowMessageModal(false)}
        title={notification.title}
        size="md"
      >
        <p className="text-sm text-gray-800 whitespace-pre-wrap break-words leading-relaxed">
          {notification.message}
        </p>
      </Modal>

      <PushDetailsModal
        isOpen={showPushDetails}
        onClose={() => setShowPushDetails(false)}
        title={notification.title}
        iconUrl={notification.icon_url}
        ttl={effectiveTtl}
      />
    </>
  );
}

export const NotificationItem = memo(NotificationItemComponent);
export default NotificationItem;
